import { ComponentTypes } from "entities/template/lib/const/componentTypes";
import { isServerError } from "shared/lib/services/base-rest-service/server-error";
import type {
    IAutonumberCounter,
    IAutonumberCounterSaveRequest,
    ITemplateAutonumberComponent,
    ITemplateComponent,
    ITemplatesService
} from "entities/template/lib/types/template_service_types";
import {
    autonumberComponentToSaveRequest,
    isAutonumberCounterChanged
} from "./autonumber";

type AutonumberSaveService = Pick<
    ITemplatesService,
    "loadAutonumberCounters" | "saveAutonumberCounter"
>;

type AutonumberSaveProgress = (components: ITemplateComponent[]) => void;

export class AutonumberNameConflictError extends Error {
    constructor(public readonly counterName: string) {
        super("Autonumber name is already taken");
        this.name = "AutonumberNameConflictError";
    }
}

const normalize = (value: IAutonumberCounterSaveRequest) => ({
    name: value.name.trim(),
    tag: value.tag.trim(),
    mask: value.mask,
    code: value.code?.trim() || "",
    date_tag: value.date_tag || "",
    is_attr_used: value.is_attr_used
});

const counterMatchesRequest = (
    counter: IAutonumberCounter,
    request: IAutonumberCounterSaveRequest
): boolean => JSON.stringify(normalize({
    name: counter.name,
    tag: counter.tag,
    mask: counter.mask || "{{number}}",
    code: counter.code || undefined,
    date_tag: counter.date_tag || undefined,
    is_attr_used: counter.is_attr_used
})) === JSON.stringify(normalize(request));

const snapshotFromRequest = (
    id: number,
    login: string,
    request: IAutonumberCounterSaveRequest
): IAutonumberCounter => ({
    id,
    login,
    name: request.name,
    tag: request.tag,
    mask: request.mask,
    code: request.code || null,
    date_tag: request.date_tag || null,
    is_attr_used: request.is_attr_used
});

const saveAutonumberComponent = async (
    component: ITemplateAutonumberComponent,
    components: ITemplateComponent[],
    login: string,
    service: AutonumberSaveService
): Promise<ITemplateAutonumberComponent> => {
    const request = autonumberComponentToSaveRequest(component, components);
    let id = component.autonumberId;
    try {
        id = Number(await service.saveAutonumberCounter(request));
    } catch (error) {
        // The current backend maps the unique-name constraint to this HTTP 400 response.
        // Other validation errors must not be presented as a name conflict.
        if (isServerError<{ message?: string }>(error) && error.status === 400 &&
            error.response?.message === "Такая запись уже существует") {
            throw new AutonumberNameConflictError(request.name);
        }
        if (component.autonumberId !== undefined || (error as { status?: number }).status) {
            throw error;
        }

        try {
            const counters = await service.loadAutonumberCounters({ name: request.name });
            const recovered = counters.find(counter =>
                counter.login === login && counterMatchesRequest(counter, request)
            );
            if (!recovered) {
                throw error;
            }
            id = recovered.id;
        } catch {
            throw error;
        }
    }

    if (id === undefined || !Number.isFinite(id)) {
        throw new Error("Autonumber API did not return a valid counter id");
    }

    return {
        ...component,
        autonumberId: id,
        initialCounter: snapshotFromRequest(id, login, { ...request, id }),
        counterLoadError: undefined
    };
};

export const getChangedExistingAutonumberComponents = (
    components: ITemplateComponent[]
): ITemplateAutonumberComponent[] => components
    .filter(item => item.type === ComponentTypes.autonumber)
    .map(item => item as ITemplateAutonumberComponent)
    .filter(item => item.autonumberId !== undefined && isAutonumberCounterChanged(item, components));

export const prepareNewAutonumberComponentsForTemplateSave = async (
    components: ITemplateComponent[],
    login: string,
    service: AutonumberSaveService,
    onProgress?: AutonumberSaveProgress
): Promise<ITemplateComponent[]> => {
    const preparedComponents = [...components];

    for (let index = 0; index < preparedComponents.length; index += 1) {
        const item = preparedComponents[index];
        if (item.type !== ComponentTypes.autonumber) {
            continue;
        }

        const component = item as ITemplateAutonumberComponent;
        if (component.autonumberId !== undefined) {
            continue;
        }

        preparedComponents[index] = await saveAutonumberComponent(
            component,
            preparedComponents,
            login,
            service
        );
        onProgress?.([...preparedComponents]);
    }

    return preparedComponents;
};

export const saveChangedExistingAutonumberComponentsAfterTemplateSave = async (
    components: ITemplateComponent[],
    login: string,
    service: AutonumberSaveService
): Promise<ITemplateComponent[]> => Promise.all(components.map(async item => {
    if (item.type !== ComponentTypes.autonumber) {
        return item;
    }

    const component = item as ITemplateAutonumberComponent;
    if (
        component.autonumberId === undefined ||
        component.counterReadOnly ||
        (component.initialCounter && component.initialCounter.login !== login) ||
        component.counterLoadError ||
        !isAutonumberCounterChanged(component, components)
    ) {
        return component;
    }

    return saveAutonumberComponent(component, components, login, service);
}));
